import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import "./toast.css";

const ToastContext = createContext({ showToast: () => {} });

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ title, description, variant = "info", duration = 5000 } = {}) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [
        ...prev,
        { id, title, description, variant, duration },
      ]);
      return id;
    },
    []
  );

  useEffect(() => {
    const cleanups = toasts.map((t) => {
      const timer = setTimeout(() => remove(t.id), t.duration ?? 3500);
      return () => clearTimeout(timer);
    });
    return () => cleanups.forEach((fn) => fn());
  }, [toasts, remove]);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* 🔒 Scoped root wrapper */}
      <div className="tv-toast-root">
        <div
          className="tv-toast-viewport"
          aria-live="polite"
          role="status"
        >
          {toasts.map((t) => (
            <div key={t.id} className={`tv-toast tv-toast--${t.variant}`}>
              {t.title && <div className="tv-toast-title">{t.title}</div>}
              {t.description && (
                <div className="tv-toast-desc">{t.description}</div>
              )}
              <button
                className="tv-toast-close"
                onClick={() => remove(t.id)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
